/**
 * Analysis bridge — wires Phase 1 analysis modules (focal points, surge detection,
 * convergence) into active investigations as contextual signals.
 *
 * Analysis events are read-only overlays: they do not inject entities or modify
 * the investigation graph. They provide situational context that appears as
 * advisory cards in the graph panel sidebar.
 */

import { eventBus } from '@/core/events';
import { addLogEntry } from './investigation-store';
import type { InvestigationState } from './types';

// ---------------------------------------------------------------------------
// Active investigation scope tracking
// ---------------------------------------------------------------------------

interface ActiveInvestigationScope {
  investigationId: string;
  /** Current pipeline state — used for log entries */
  pipelineState: InvestigationState;
  /** Bounding box [south, west, north, east] for geographic overlap check */
  geographicBbox?: [number, number, number, number];
  /** Keywords from scope contract + subject description */
  keywords: string[];
}

let activeScopes: ActiveInvestigationScope[] = [];

export function registerInvestigationScope(scope: ActiveInvestigationScope): void {
  // Avoid duplicates
  unregisterInvestigationScope(scope.investigationId);
  activeScopes.push(scope);
}

export function unregisterInvestigationScope(investigationId: string): void {
  activeScopes = activeScopes.filter(s => s.investigationId !== investigationId);
}

export function getActiveScopes(): ReadonlyArray<ActiveInvestigationScope> {
  return activeScopes;
}

// ---------------------------------------------------------------------------
// Signal matching
// ---------------------------------------------------------------------------

function matchesScope(signal: unknown, scope: ActiveInvestigationScope): boolean {
  if (scope.keywords.length === 0) return false;

  // Stringify the signal and check for keyword overlap
  const signalText = JSON.stringify(signal).toLowerCase();
  return scope.keywords.some(kw => signalText.includes(kw.toLowerCase()));
}

function emitContextSignal(
  investigationId: string,
  pipelineState: InvestigationState,
  signalType: string,
  data: unknown,
): void {
  const summary = `${signalType} detected matching investigation scope`;

  eventBus.emit('investigation:context-signal', {
    investigationId,
    signalType,
    summary,
    data,
  });

  addLogEntry({
    investigationId,
    state: pipelineState,
    action: 'context_signal',
    detail: { signalType, data: data ?? {} },
  }).catch(err => {
    console.error('[analysis-bridge] Failed to log context signal:', err);
  });
}

// ---------------------------------------------------------------------------
// Initialization — subscribe to Phase 1 analysis events
// ---------------------------------------------------------------------------

let initialized = false;

export function initAnalysisBridge(): void {
  if (initialized) return;
  initialized = true;

  eventBus.on('analysis:focal-point', (focalPoint) => {
    for (const scope of activeScopes) {
      if (matchesScope(focalPoint, scope)) {
        emitContextSignal(scope.investigationId, scope.pipelineState, 'focal_point', focalPoint);
      }
    }
  });

  eventBus.on('analysis:surge', (surge) => {
    for (const scope of activeScopes) {
      if (matchesScope(surge, scope)) {
        emitContextSignal(scope.investigationId, scope.pipelineState, 'surge', surge);
      }
    }
  });

  eventBus.on('analysis:convergence', (convergence) => {
    for (const scope of activeScopes) {
      if (matchesScope(convergence, scope)) {
        emitContextSignal(scope.investigationId, scope.pipelineState, 'convergence', convergence);
      }
    }
  });
}
